import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './RewildingInfoBox.module.css';
import FilesBox from './FilesBox';

const MAX_UPLOAD_FILES = 5;
const ACCEPT_PDF = 'application/pdf';
const ALLOWED_TYPES = ['application/pdf'];
const MAX_FILE_SIZE_MB = 50;

const RewildingInfoBox = props => {
  const intl = useIntl();

  const {
    rewildingInfoFiles,
    onAddRewildingInfoFiles,
    onRemoveRewildingInfoFile,
    showTitle = false,
    isAdminFlow = false,
    userId = null,
    removeFileInProgress = false,
  } = props;

  const [fileUploadRequested, setFileUploadRequested] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errorTexts, setErrorTexts] = useState([]);

  const rewildingInfoFilesByAdmin = rewildingInfoFiles.filter(item => item.createdBy === 'admin');
  const rewildingInfoFilesByProvider = rewildingInfoFiles.filter(
    item => item.createdBy === 'provider'
  );

  const inputDisabled = fileUploadRequested;

  const handleFiles = async (files, inputEl) => {
    const errors = [];

    if (files.length > MAX_UPLOAD_FILES) {
      errors.push(
        intl.formatMessage(
          { id: 'RewildingInfoBox.invalidMaxNumberOfFiles' },
          { maxUploaded: MAX_UPLOAD_FILES }
        )
      );
      setErrorTexts(errors);
      if (inputEl) inputEl.value = '';
      return;
    }

    const validFiles = files.filter(file => {
      const isValidType = ALLOWED_TYPES.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!isValidType) {
        errors.push(
          intl.formatMessage({ id: 'RewildingInfoBox.invalidFileType' }, { fileName: file.name })
        );
      }

      if (!isValidSize) {
        errors.push(
          intl.formatMessage(
            { id: 'RewildingInfoBox.invalidMaxSize' },
            { fileName: file.name, maxFileSize: MAX_FILE_SIZE_MB }
          )
        );
      }

      return isValidType && isValidSize;
    });

    setErrorTexts(errors);

    if (validFiles.length === 0) {
      if (inputEl) inputEl.value = '';
      return;
    }

    setFileUploadRequested(true);

    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('userFlow', isAdminFlow ? 'admin' : 'provider');
      formData.append('userId', userId);

      await onAddRewildingInfoFiles({ formData });
    } finally {
      setFileUploadRequested(false);
      if (inputEl) inputEl.value = '';
    }
  };

  const onChange = e => {
    const inputEl = e.target;
    const files = Array.from(inputEl.files);
    handleFiles(files, inputEl);
  };

  const onDrop = e => {
    e.preventDefault();
    if (inputDisabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const onDragOver = e => {
    e.preventDefault();
  };

  const showErrors = errorTexts.map(item => {
    const id = uuidv4();
    return <p key={id}>{item}</p>;
  });

  return (
    <div className={css.wrapper}>
      {showTitle ? (
        <div className={css.titleBox}>
          <p className={css.title}>
            <FormattedMessage id="RewildingInfoBox.rewildingInfoTitle" />
          </p>
          <p className={css.description}>
            <FormattedMessage id="RewildingInfoBox.rewildingInfoTip" />
          </p>
        </div>
      ) : null}
      <div
        className={css.inputBox}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
      >
        <input
          className={css.inputHidden}
          id="uploadFiles"
          type="file"
          onChange={onChange}
          accept={ACCEPT_PDF}
          multiple
          disabled={inputDisabled}
        />
        <label htmlFor="uploadFiles">
          <div
            className={classNames(css.inputLabel, {
              [css.inputLabelDragging]: isDragging,
            })}
          >
            <p className={css.inputText}>
              <FormattedMessage id="RewildingInfoBox.rewildingInfoInputTop" />
            </p>
            <p className={css.inputText}>
              <FormattedMessage id="RewildingInfoBox.rewildingInfoInputBottom" />
            </p>
            <p className={css.inputRules}>
              <FormattedMessage id="RewildingInfoBox.rewildingInfoInputRules" />
            </p>
          </div>
        </label>
      </div>

      {errorTexts.length > 0 && <div className={css.errors}>{showErrors}</div>}

      {(isAdminFlow
        ? rewildingInfoFilesByAdmin.length > 0
        : rewildingInfoFilesByProvider.length > 0) && (
        <FilesBox
          files={isAdminFlow ? rewildingInfoFilesByAdmin : rewildingInfoFilesByProvider}
          flow={isAdminFlow ? 'admin' : 'provider'}
          onRemoveRewildingInfoFile={onRemoveRewildingInfoFile}
          removeFileInProgress={removeFileInProgress}
        />
      )}

      {!isAdminFlow && rewildingInfoFilesByAdmin.length > 0 ? (
        <div className={css.filesBoxWrapper}>
          <p className={css.filesBoxDescription}>
            <FormattedMessage id="RewildingInfoBox.rewildingInfoFilesByAdmin" />
          </p>
          <FilesBox files={rewildingInfoFilesByAdmin} flow={'provider'} />
        </div>
      ) : null}
    </div>
  );
};

export default RewildingInfoBox;
